import React, { useEffect, useState } from "react";
import { Avatar, Breadcrumb, Button, Col, List, Row, Typography, Modal, Tag } from "antd";
import ActivityForm from "./ActivityForm";
import { error, success } from "../../../common/Alerts/Message";
import {
  publishCampaign,
  SponsorAddActivity,
  SponsorDeleteActivity,
  SponsorUpdateActivity
} from "../../../Config/API/sponsor";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

function CampaignActivity({ campaign, panels }) {
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [showActivityForm, setshowActivityForm] = useState(false);
  const navigate = useNavigate();
  const newActivity = {
    name: "",
    activityType: ""
  };
  const [activities, setActivities] = useState([newActivity]);

  const handleAddUpdateActivity = async (values, { resetForm }) => {
    setLoading(true);
    const isEdit = values?.activityId;

    try {
      let response = null;
      let data = null;
      if (values.panel === "") {
        delete values?.panel;
      }
      if (isEdit) {
        // update activity
        response = await SponsorUpdateActivity({
          ...values,
          time: values?.time ? dayjs(values?.time).format("hh:mm") : ""
        });
        data = await response.json();
      } else {
        // delete activityId key from payload incase of add
        delete values?.activityId;
        // add activity
        response = await SponsorAddActivity({
          ...values,
          time: values?.time ? dayjs(values?.time).format("hh:mm") : ""
        });
        data = await response.json();
      }
      if (data?.type === "success") {
        let updatedActivities = [...activities];

        if (isEdit) {
          updatedActivities[current] = {
            ...updatedActivities[current],
            ...data?.activity
          };
        } else {
          updatedActivities = updatedActivities?.filter(
            (activity) => activity?.name !== "" && activity?.activityType !== ""
          );
          updatedActivities = [...updatedActivities, data?.activity];
          updatedActivities.push(newActivity);
        }

        setActivities(updatedActivities);
        setCurrent(current + 1);
        success(`Activity ${isEdit ? "updated" : "added"} successfully`);
        setLoading(false);
        resetForm();
      } else {
        setLoading(false);
        error("Error occurred while adding/updating activity");
      }
    } catch (e) {
      setLoading(false);
      error(e.message);
    }
  };

  const handleDeleteActivity = async (id) => {
    try {
      setDeleteLoading(true);
      const response = await SponsorDeleteActivity(id);
      const data = await response.json();
      if (data?.type === "success") {
        const updated = activities?.filter((activity) => activity?._id !== id);
        setActivities(updated);
        success("Activity Deleted Successfully");
      }
      setDeleteLoading(false);
    } catch (error) {
      setDeleteLoading(false);
      error(error.message);
    }
  };

  const handleConfirmPublish = async () => {
    setPublishLoading(true);
    try {
      const response = await publishCampaign({ campaignId: campaign?._id });
      const data = await response.json();

      if (data?.type === "success") {
        success("Campaign published successfully");
        navigate(`/sponsor/campaigns`, { replace: true });
      } else {
        error("Failed to publish campaign");
      }
    } catch (err) {
      error(err.message);
    } finally {
      setPublishLoading(false);
    }
  };

  const showConfirm = () => {
    Modal.confirm({
      title: "Are you sure you want to publish this campaign?",
      content:
        "Upon proceeding, the campaign will be public and visible to all users on the platform.",
      okText: "Proceed",
      cancelText: "Cancel",
      onOk() {
        handleConfirmPublish();
      }
    });
  };

  useEffect(() => {
    if (campaign && campaign?.activities) {
      setActivities([...campaign.activities, newActivity]);
    }
  }, [campaign?.activities]);

  return (
    <div className="p-5">
      <Row>
        <Col span={10}>
          <h1>Campaign Name</h1>
          <p>{campaign?.name}</p>
        </Col>
        <Col span={10}>
          <h1>Campaign Description</h1>
          <p>{campaign?.description}</p>
        </Col>

        {!campaign?.isActive && (
          <Col span={2}>
            <Button
              loading={publishLoading}
              onClick={showConfirm}
              style={{ maxWidth: "280px", margin: "0" }}
              disabled={
                !activities.some(
                  (activity) => activity?.panel && Object.keys(activity?.panel)?.length > 0
                )
              }
              className="ceprimarybutton blueButton"
              type="primary">
              Publish
            </Button>
          </Col>
        )}
      </Row>
      <hr className="mt-5 mb-5" />
      <Typography.Title type="secondary">Added Activities</Typography.Title>
      <Col xs={24} md={24} className="formorganizer p-3">
        <List
          itemLayout="horizontal"
          dataSource={activities?.filter((activity) => activity?._id)}
          renderItem={(item, index) => (
            <List.Item
              aria-disabled={deleteLoading ? true : false}
              actions={[
                <Button
                  key="view-activity"
                  type="primary"
                  style={{
                    backgroundColor: "#02c171",
                    borderRadius: "8px",
                    padding: "0 16px",
                    marginRight: "8px"
                  }}
                  onClick={() => {
                    setCurrent(index);
                    setshowActivityForm(true);
                  }}>
                  View
                </Button>,
                <Button
                  key="delete-activity"
                  type="primary"
                  danger
                  style={{
                    borderRadius: "8px",
                    padding: "0 16px"
                  }}
                  loading={deleteLoading}
                  onClick={deleteLoading ? null : () => handleDeleteActivity(item?._id)}>
                  Delete
                </Button>
              ]}>
              <List.Item.Meta
                avatar={
                  <Avatar style={{ backgroundColor: "#b1b1b1", verticalAlign: "middle" }}>
                    {item?.name?.charAt(0).toUpperCase() || "A"}
                  </Avatar>
                }
                title={
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span>{item?.name}</span>
                    <Tag
                      color={item?.isActive || item?.isApproved ? "green" : "red"}
                      style={{ borderRadius: "6px", marginLeft: "1px" }}>
                      {item?.isActive || item?.isApproved ? "Complete" : "Incomplete"}
                    </Tag>
                  </div>
                }
                description={`Activity Type: ${item?.activityType}`}
              />
            </List.Item>
          )}
        />
      </Col>

      <hr className="mt-5" />
      {/* Render the form or activity content based on the current activity */}
      <Breadcrumb separator=">" style={{ padding: "0 0 2% 2.5%" }}>
        {activities?.map((activity, index) => {
          return (
            <Breadcrumb.Item
              key={index}
              className={"activecrumb"}
              onClick={() => {
                setCurrent(index);
                setshowActivityForm(true);
              }}>
              <span style={{ cursor: "pointer" }}>{activity?.name || "New"}</span>
            </Breadcrumb.Item>
          );
        })}
      </Breadcrumb>
      <div className="formorganizer  p-5">
        {showActivityForm && (
          <ActivityForm
            handleSubmit={handleAddUpdateActivity}
            loading={loading}
            campaignId={campaign?._id}
            panels={panels}
            viewActivity={activities?.[current]}
          />
        )}
        <Button
          className="mt-5 draftbutton text-center"
          style={{ marginLeft: "0" }}
          type="primary"
          onClick={() => {
            setshowActivityForm((pre) => !pre);
            setCurrent(activities.length);
          }}>
          + New Activity
        </Button>
      </div>
    </div>
  );
}

export default CampaignActivity;
